import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PageLayout } from '@cvx/nextpage';
import { trigger, transition, style, animate, query, animateChild, group } from '@angular/animations';
import {
    AttestationService,
    AttestationBatchContextResponse,
    AttestationContextResponse,
    AttestationEvaluateResponse
} from 'src/app/services/attestation/Attestation.service';

type PermissionState = 'idle' | 'evaluating' | 'evaluated_approved' | 'evaluated_rejected' | 'evaluation_unavailable' | 'escalating' | 'submitting' | 'done';

export interface PermissionViewModel {
    context: AttestationContextResponse;
    state: PermissionState;
    justification: string;
    evalResult?: AttestationEvaluateResponse;
    attempts: number;
    finalDecision?: string;
    escalationReason: string;
    error?: string;
    pendingAction?: 'keep' | 'remove';
}

@Component({
    selector: 'app-attest',
    templateUrl: './attest.component.html',
    animations: [
        trigger('cardSlide', [
            transition('* => next', [
                style({ opacity: 0, transform: 'translateX(40px)' }),
                animate('220ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
            ]),
            transition('* => prev', [
                style({ opacity: 0, transform: 'translateX(-40px)' }),
                animate('220ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
            ])
        ]),
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-6px)' }),
                animate('180ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
            ])
        ])
    ]
})
export class AttestComponent implements OnInit {

    PageLayout = PageLayout;

    campaignId: string;
    spObjectId: string;

    isLoading = true;
    loadError: string;

    batchContext: AttestationBatchContextResponse;
    fullCyclePermissions: PermissionViewModel[] = [];
    currentIndex = 0;
    slideDir: 'next' | 'prev' | null = null;
    showGuide = false;

    private advanceAfterDone(): void {
        const next = this.fullCyclePermissions.findIndex((p, i) => i > this.currentIndex && p.state !== 'done');
        if (next !== -1) setTimeout(() => { this.slideDir = 'next'; this.currentIndex = next; }, 700);
    }

    get currentVm(): PermissionViewModel | undefined {
        return this.fullCyclePermissions[this.currentIndex];
    }

    prevPermission(): void {
        if (this.currentIndex > 0) { this.slideDir = 'prev'; this.currentIndex--; }
    }

    nextPermission(): void {
        if (this.currentIndex < this.fullCyclePermissions.length - 1) { this.slideDir = 'next'; this.currentIndex++; }
    }

    get allPermissions(): PermissionViewModel[] {
        return this.fullCyclePermissions;
    }

    get completedCount(): number {
        return this.allPermissions.filter(p => p.state === 'done').length;
    }

    get allDone(): boolean {
        return this.allPermissions.length > 0 && this.completedCount === this.allPermissions.length;
    }

    constructor(
        private route: ActivatedRoute,
        private attestationService: AttestationService
    ) {}

    async ngOnInit() {
        this.campaignId = this.route.snapshot.queryParamMap.get('campaignId') ?? '';
        this.spObjectId = this.route.snapshot.queryParamMap.get('spObjectId') ?? '';

        if (!this.campaignId || !this.spObjectId) {
            this.loadError = 'Missing required parameters. Please use the link from your notification email.';
            this.isLoading = false;
            return;
        }

        try {
            this.batchContext = await this.attestationService.getBatchContext(this.campaignId, this.spObjectId);
            // full-cycle first (need justification), renewals after — one unified carousel
            this.fullCyclePermissions = [
                ...(this.batchContext.fullCyclePermissions ?? []).map(c => this.toViewModel(c)),
                ...(this.batchContext.lightweightPermissions ?? []).map(c => this.toViewModel(c))
            ];
        } catch (err) {
            this.loadError = 'Failed to load your attestation. Please try again or contact support if the issue persists.';
        } finally {
            this.isLoading = false;
        }
    }

    private toViewModel(context: AttestationContextResponse): PermissionViewModel {
        const wasEscalated = context.previousCycleStatus === 'Escalated';
        // confirmed or removed in this campaign cycle: stay in the carousel as a locked done card
        const doneThisCycle = context.isFullCycle &&
            (context.previousCycleStatus === 'Confirmed' || context.previousCycleStatus === 'Removed');
        const isDone = wasEscalated || doneThisCycle;
        return {
            context,
            state: isDone ? 'done' : 'idle',
            justification: '',
            attempts: 0,
            escalationReason: '',
            finalDecision: wasEscalated ? 'Escalated'
                : doneThisCycle ? context.previousCycleStatus
                : undefined
        };
    }

    async confirmRenewal(vm: PermissionViewModel, keep: boolean) {
        vm.pendingAction = undefined;
        vm.state = 'submitting';
        vm.error = undefined;
        try {
            await this.attestationService.submit({
                servicePrincipalObjectId: this.spObjectId,
                campaignId: this.campaignId,
                permissionId: vm.context.permissionId,
                resourceName: vm.context.resourceName,
                resourceServicePrincipalId: vm.context.resourceServicePrincipalId,
                justification: vm.context.previousJustification ?? 'Previously attested — confirmed unchanged',
                isFullCycle: false,
                cycleStatus: keep ? 'Confirmed' : 'Removed'
            });
            vm.finalDecision = keep ? 'Confirmed' : 'Removed';
            vm.state = 'done';
            this.advanceAfterDone();
        } catch (err) {
            vm.error = 'Submission failed. Please try again.';
            vm.state = 'idle';
        }
    }

    async evaluate(vm: PermissionViewModel) {
        if (!vm.justification?.trim()) return;
        vm.state = 'evaluating';
        vm.error = undefined;
        try {
            vm.evalResult = await this.attestationService.evaluate({
                permissionName: vm.context.permissionName,
                justification: vm.justification,
                servicePrincipalObjectId: this.spObjectId,
                permissionId: vm.context.permissionId,
                resourceName: vm.context.resourceName,
                resourceServicePrincipalId: vm.context.resourceServicePrincipalId,
                campaignId: this.campaignId
            });
            vm.attempts++;
            vm.state = vm.evalResult.decision === 'Accepted' ? 'evaluated_approved' : 'evaluated_rejected';
        } catch (err) {
            // AI evaluation is unavailable (network/service error) — the user already attempted a
            // review, which satisfies the mandatory-evaluation requirement. Don't block them here;
            // let them proceed to keep/remove, or retry evaluation once the service recovers.
            vm.attempts++;
            vm.evalResult = undefined;
            vm.state = 'evaluation_unavailable';
        }
    }

    retryEvaluation(vm: PermissionViewModel) {
        // keep vm.evalResult so feedback stays visible while user rewrites
        vm.state = 'idle';
    }

    startEscalation(vm: PermissionViewModel) {
        vm.error = undefined;
        vm.state = 'escalating';
    }

    cancelEscalation(vm: PermissionViewModel) {
        vm.state = 'evaluated_rejected';
    }

    async submitFullCycle(vm: PermissionViewModel, keep: boolean) {
        const previousState = vm.state;
        vm.pendingAction = undefined;
        vm.state = 'submitting';
        vm.error = undefined;
        try {
            await this.attestationService.submit({
                servicePrincipalObjectId: this.spObjectId,
                campaignId: this.campaignId,
                permissionId: vm.context.permissionId,
                resourceName: vm.context.resourceName,
                resourceServicePrincipalId: vm.context.resourceServicePrincipalId,
                justification: vm.justification,
                isFullCycle: true,
                cycleStatus: keep ? 'Confirmed' : 'Removed'
            });
            vm.finalDecision = keep ? 'Confirmed' : 'Removed';
            vm.state = 'done';
            this.advanceAfterDone();
        } catch (err) {
            vm.error = 'Submission failed. Please try again.';
            vm.state = previousState;
        }
    }

    async submitEscalation(vm: PermissionViewModel) {
        vm.state = 'submitting';
        vm.error = undefined;
        try {
            await this.attestationService.escalate({
                servicePrincipalObjectId: this.spObjectId,
                campaignId: this.campaignId,
                permissionId: vm.context.permissionId,
                resourceName: vm.context.resourceName,
                resourceServicePrincipalId: vm.context.resourceServicePrincipalId,
                justification: vm.justification,
                isFullCycle: true,
                cycleStatus: 'Escalated',
                escalationReason: vm.escalationReason || undefined
            });
            vm.finalDecision = 'Escalated';
            vm.state = 'done';
            this.advanceAfterDone();
        } catch (err) {
            vm.error = 'Escalation failed. Please try again.';
            vm.state = 'escalating';
        }
    }

    submittedJustification(vm: PermissionViewModel): string | undefined {
        return vm.justification?.trim() || vm.context.previousJustification || undefined;
    }

    decisionBadgeClass(decision: string | undefined): string {
        switch (decision) {
            case 'Confirmed': return 'badge bg-success';
            case 'Removed':   return 'badge bg-warning text-dark';
            case 'Escalated': return 'badge bg-secondary';
            default:          return 'badge bg-light text-dark';
        }
    }
}
